/**
 * GarageScreen — minimal port of garageiq-web/app/(main)/garage/GarageClient.jsx.
 *
 * The web GarageClient.jsx is the heaviest screen in the codebase (multi-vehicle
 * mileage tracker, maintenance log, cost tracker, scheduler embeds, diagnostics
 * pivot). Porting all of it cleanly is a substantial follow-up — see the
 * "Remaining work" section in RUNNING.md. This MVP version covers the core
 * loop: list saved vehicles, pick the active one, add a new vehicle, remove.
 *
 * What's deferred to v2:
 *  - Mileage tracker + per-item maintenance schedule (uses
 *    lib/maintenanceSchedule.js + lib/guideMaintenanceMap.js — both shipped).
 *  - Cost / log history view (data already lives in `garageiq_maint_log_*`
 *    keys; a list view is straightforward).
 *  - Embedded diagnostics + ShopScheduler.
 */
import React from 'react';
import { Alert, FlatList, Pressable, Text, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';

import { useGarage } from '../lib/useGarage';
import { formatVehicle } from '../lib/vehicleData';
import { IconCar, IconGarage } from '../components/Icons';

function VehicleRow({ vehicle, isActive, onActivate, onRemove }) {
  return (
    <Pressable
      onPress={onActivate}
      className={`bg-charcoal/40 border rounded-2xl p-5 mb-3 active:opacity-80 ${
        isActive ? 'border-sky/60' : 'border-white/10'
      }`}
    >
      <View className="flex-row items-center justify-between">
        <View className="flex-row items-center gap-3 flex-1">
          <IconCar size={24} />
          <View className="flex-1">
            <Text className="text-base font-black text-white">{formatVehicle(vehicle)}</Text>
            {!!vehicle.engineLabel && (
              <Text className="text-xs text-white/40 mt-0.5">{vehicle.engineLabel}</Text>
            )}
            {!!vehicle.vin && (
              <Text className="text-[10px] text-white/30 mt-0.5">VIN {vehicle.vin}</Text>
            )}
          </View>
        </View>
        {isActive && (
          <View className="bg-sky px-2.5 py-0.5 rounded-full">
            <Text className="text-white text-[10px] font-black">ACTIVE</Text>
          </View>
        )}
      </View>
      <View className="flex-row gap-2 mt-3 pt-3 border-t border-white/10">
        {!isActive && (
          <Pressable onPress={onActivate} className="bg-white/5 border border-white/10 px-3 py-1.5 rounded-lg active:opacity-80">
            <Text className="text-white/70 text-xs font-bold">Set Active</Text>
          </Pressable>
        )}
        <Pressable onPress={onRemove} className="bg-red-500/10 border border-red-500/20 px-3 py-1.5 rounded-lg active:opacity-80">
          <Text className="text-red-400 text-xs font-bold">Remove</Text>
        </Pressable>
      </View>
    </Pressable>
  );
}

export default function GarageScreen() {
  const navigation = useNavigation();
  const { garage, activeIdx, setActive, removeVehicle, mounted } = useGarage();

  if (!mounted) {
    return <View className="flex-1 bg-navy" />;
  }

  return (
    <View className="flex-1 bg-navy">
      <View className="px-5 pt-5 pb-3">
        <View className="flex-row items-center gap-2 mb-1">
          <IconGarage size={24} />
          <Text className="text-2xl font-black text-white">My Garage</Text>
        </View>
        <Text className="text-white/40 text-sm">
          {garage.length === 0
            ? 'Add a vehicle to get tailored maintenance guides.'
            : `${garage.length} vehicle${garage.length === 1 ? '' : 's'} · tap a card to set it active.`}
        </Text>
      </View>

      {garage.length === 0 ? (
        <View className="flex-1 items-center justify-center px-6">
          <View className="bg-charcoal/40 border border-white/10 rounded-2xl px-6 py-12 items-center w-full">
            <IconCar size={56} />
            <Text className="text-xl font-black text-white mt-4 mb-2">No vehicles yet</Text>
            <Text className="text-white/50 text-center mb-6">
              Add your first vehicle to start browsing guides built specifically for your car.
            </Text>
            <Pressable
              onPress={() => navigation.navigate('VehicleSelector')}
              className="bg-sky px-7 py-3 rounded-xl active:opacity-80"
            >
              <Text className="text-white font-bold">+ Add Your First Vehicle</Text>
            </Pressable>
          </View>
        </View>
      ) : (
        <FlatList
          data={garage}
          keyExtractor={(_, i) => String(i)}
          contentContainerStyle={{ padding: 20, paddingBottom: 100 }}
          renderItem={({ item, index }) => (
            <VehicleRow
              vehicle={item}
              isActive={index === activeIdx}
              onActivate={() => setActive(index)}
              onRemove={() =>
                Alert.alert(
                  'Remove vehicle?',
                  `${formatVehicle(item)} will be removed from your garage.`,
                  [
                    { text: 'Cancel', style: 'cancel' },
                    { text: 'Remove', style: 'destructive', onPress: () => removeVehicle(index) },
                  ],
                )
              }
            />
          )}
          ListFooterComponent={
            <Pressable
              onPress={() => navigation.navigate('VehicleSelector')}
              className="bg-sky py-4 rounded-2xl items-center mt-2 active:opacity-80"
            >
              <Text className="text-white font-black text-lg">+ Add Another Vehicle</Text>
            </Pressable>
          }
        />
      )}

      {/* Quick links to deferred features so the user knows where the rest lives. */}
      <View className="absolute bottom-0 left-0 right-0 bg-charcoal/60 border-t border-white/10 px-5 py-3">
        <Text className="text-[10px] text-white/40 uppercase tracking-widest font-bold mb-1">Coming in v2</Text>
        <Text className="text-xs text-white/50 leading-4">
          Mileage tracker · Maintenance log · Schedule reminders · Embedded diagnostics
          {' · '}<Text className="text-sky">See web app for full feature set.</Text>
        </Text>
      </View>
    </View>
  );
}
